import React, { useState, useMemo, useCallback } from 'react';
import './BioWindow.css';
import teardropGif from './assets/player-select/teardrop_rotate.GIF';
import chickenGif from './assets/player-select/Chicken.GIF';
import custardBg from './assets/player-select/worlds-finest-frozen-custard.webp';
import chickenBg from './assets/player-select/chicken.webp';
// Static, hand-cropped front-facing frames for the thumb selector buttons.
// The animated GIFs rotate around an off-center pivot (near each guy's
// left ear), so front/back poses drift a few px off-center in opposite
// directions in a small circular crop -- a static frame sidesteps that
// entirely and is always dead-centered.
import teardropThumb from './assets/player-select/teardrop-thumb.webp';
import chickenThumb from './assets/player-select/chicken-thumb.webp';

const bios = [
  {
    id: 'teardrop',
    title: 'Jacob O’Brien',
    codename: 'Mr. Teardrop',
    birthplace: 'Rochester, New York',
    education: 'Motion Picture Institute of Michigan',
    projects: [
      { title: 'Did You Guys Eat?', imdb: 'https://www.imdb.com/title/tt36473630/' },
      { title: 'Future Days', imdb: 'https://www.imdb.com/title/tt21991528/' },
      { title: 'Neutrals', imdb: 'https://www.imdb.com/title/tt30761083/' },
    ],
    imdb: 'https://www.imdb.com/name/nm10018202/',
    model: teardropGif,
    thumb: teardropThumb,
    background: custardBg,
    email: 'jobrien@neutralsfilm.com',
    stats: {
      Coffee: 90,
      "Davinci Resolve": 80,
      "New iPhone": 100,
      '"Slap Da Bass"': 50,
      "Shoe Collection": 65,
      "Band Shirts": 70,
      "Video Store Clerk": 100,
    },
    description: <>Director, producer, editor. A multi-faceted filmmaker, O’Brien’s work reflects the lives of the overlooked and the underdogs. He directed the regional documentary <a href="https://www.imdb.com/title/tt21991528/" target="_blank" rel="noopener noreferrer"><em>Future Days</em></a> (2022) for B’s Music Shop, co-produced and edited <a href="https://www.imdb.com/title/tt36473630/" target="_blank" rel="noopener noreferrer"><em>Did You Guys Eat?</em></a> (2025) for Alma College, and produced Horne’s <a href="https://www.imdb.com/title/tt30761083/" target="_blank" rel="noopener noreferrer"><em>Neutrals</em></a>.</>,
  },
  {
    id: 'chicken',
    title: 'Jacob Horne',
    codename: 'JH_Chicken',
    birthplace: 'Clinton Township, Michigan',
    education: 'Motion Picture Institute of Michigan',
    projects: [
      { title: 'Neutrals', imdb: 'https://www.imdb.com/title/tt30761083/' },
      { title: '4AM', imdb: 'https://www.imdb.com/title/tt12755900/' },
    ],
    imdb: 'https://www.imdb.com/name/nm7007630/',
    model: chickenGif,
    thumb: chickenThumb,
    background: chickenBg,
    email: 'jhorne@neutralsfilm.com',
    stats: {
      "DVD Collection": 'MAXED',
      Coffee: 'MAXED',
      "Celtx Hours": 70,
      "LEGO Builds": 60,
      "Halloween Spirit": 90,
      "Set Communication": 65,
      Mustache: 50,
    },
    description: <>Director, producer, and screenwriter. One of Detroit’s most driven up and coming filmmakers. Horne won multiple festival awards across the states for writing and directing his short film <a href="https://www.imdb.com/title/tt11125916/" target="_blank" rel="noopener noreferrer"><em>Neutrals</em></a> (2020), which was quickly fashioned into <a href="https://www.imdb.com/title/tt30761083/" target="_blank" rel="noopener noreferrer">his debut feature film</a>, slated to release this year.</>,
  }
];

// Blue / green / orange, in that order -- the site's real accent colors
// (TopBar/NewsTerminal/MusicPlayer), cycled across the stat rows so no
// single stat bar reads as "the important orange one."
const STAT_ACCENTS = ['#3d7cc2', '#48c95f', '#e0a458'];
const STAT_SEGMENTS = 10;

function StatBar({ name, value, color }) {
  const maxed = value === 'MAXED';
  const filledSegs = maxed ? STAT_SEGMENTS : Math.round(value / 100 * STAT_SEGMENTS);
  return (
    <div className="ps-stat-row">
      <span className="ps-stat-name">{name}</span>
      <div className="ps-stat-track">
        {Array.from({ length: STAT_SEGMENTS }).map((_, i) => (
          <div
            key={i}
            className={i < filledSegs ? 'ps-stat-seg filled' : 'ps-stat-seg'}
            style={i < filledSegs ? { background: color, color } : undefined}
          />
        ))}
      </div>
      <span className={maxed ? 'ps-stat-value ps-stat-maxed' : 'ps-stat-value'}>{value}</span>
    </div>
  );
}

export default function BioWindow() {
  const [activeId, setActiveId] = useState(bios[0].id);
  const activeIndex = useMemo(() => bios.findIndex(b => b.id === activeId), [activeId]);
  const bio = bios[activeIndex];
  const accent = STAT_ACCENTS[activeIndex % STAT_ACCENTS.length];

  const goTo = useCallback((i) => {
    setActiveId(bios[(i + bios.length) % bios.length].id);
  }, []);

  return (
    <div className={`bio-window ${bio.id}`}>
      <div className="ps-bg" style={{ backgroundImage: `url(${bio.background})` }} />
      <div className="ps-vignette" />
      <div className="ps-scanlines" />

      <div className="ps-hud-label"><span className="dot" />PLAYER SELECT</div>
      <div className="ps-round">
        {String(activeIndex + 1).padStart(2, '0')} / {String(bios.length).padStart(2, '0')}
      </div>

      <div className="ps-layout">
        <div className="ps-panel">
          <div key={bio.id} className="ps-panel-inner">
            <h2 className="ps-codename">{bio.codename}</h2>
            <div className="ps-realname">{bio.title}</div>

            <div className="ps-meta">
              <div className="ps-chip"><b>From</b> {bio.birthplace}</div>
              <div className="ps-chip"><b>Trained</b> {bio.education}</div>
              {bio.imdb && (
                <div className="ps-chip">
                  <a href={bio.imdb} target="_blank" rel="noopener noreferrer">IMDb ↗</a>
                </div>
              )}
              <div className="ps-chip ps-chip-wide">
                <b>Credits</b>{' '}
                {bio.projects.map((proj, idx) => (
                  <span key={idx}>
                    <a href={proj.imdb} target="_blank" rel="noopener noreferrer"><em>{proj.title}</em></a>
                    {idx < bio.projects.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            </div>

            <p className="ps-description">{bio.description}</p>

            <a href={`mailto:${bio.email}`} className="ps-cta"><span>Book Now!</span></a>

            <div className="ps-stats">
              {Object.entries(bio.stats).map(([name, value], i) => (
                <StatBar key={name} name={name} value={value} color={STAT_ACCENTS[i % STAT_ACCENTS.length]} />
              ))}
            </div>
          </div>
        </div>

        <div className="ps-character-stage">
          <div className="ps-ground-glow" style={{ background: `radial-gradient(ellipse at center, ${accent}88 0%, ${accent}22 45%, transparent 75%)` }} />
          <img key={bio.id} className="ps-character-img" src={bio.model} alt={bio.codename} draggable={false} />
        </div>
      </div>

      {bios.length > 1 && (
        <div className="ps-thumbs">
          {bios.map((b, i) => (
            <button
              key={b.id}
              className={i === activeIndex ? 'ps-thumb active' : 'ps-thumb'}
              style={{ backgroundImage: `url(${b.thumb})` }}
              onClick={() => goTo(i)}
              aria-label={`Select ${b.codename}`}
              aria-pressed={i === activeIndex}
            />
          ))}
        </div>
      )}
    </div>
  );
}
